import type { ReactNode } from 'react';

export interface Column<Row> {
  key: string;
  header: string;
  /** Figures sit on the right, so that their digits line up. */
  numeric?: boolean;
  cell: (row: Row) => ReactNode;
}

interface DataTableProps<Row> {
  caption: string;
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string | number;
  /** A last row, in bold: the totals. One cell for each column. */
  footer?: ReactNode[];
}

/** A plain table of figures. */
export default function DataTable<Row>({ caption, columns, rows, rowKey, footer }: DataTableProps<Row>) {
  // A table wider than a phone scrolls sideways inside its card.
  return (
    <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr className="border-b border-border">
          {columns.map((c) => (
            <th
              key={c.key}
              scope="col"
              className={`py-2 px-3 first:pl-0 last:pr-0 text-xs font-medium text-muted-foreground whitespace-nowrap ${
                c.numeric ? 'text-right' : 'text-left'
              }`}
            >
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((row) => (
          <tr key={rowKey(row)} className="hover:bg-accent/30 transition-colors">
            {columns.map((c) => (
              <td
                key={c.key}
                className={`py-2 px-3 first:pl-0 last:pr-0 text-foreground ${
                  c.numeric ? 'text-right tabular-nums whitespace-nowrap' : 'text-left'
                }`}
              >
                {c.cell(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {footer && (
        <tfoot>
          <tr className="border-t-2 border-border font-semibold">
            {columns.map((c, i) => (
              <td
                key={c.key}
                className={`py-2 px-3 first:pl-0 last:pr-0 text-foreground ${
                  c.numeric ? 'text-right tabular-nums whitespace-nowrap' : 'text-left'
                }`}
              >
                {footer[i]}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
    </div>
  );
}
